<!-- EditorContent.svelte - Refactored -->
<script>
    import { createEventDispatcher, onMount, onDestroy, tick } from "svelte";
    import logger from "../utils/logger";
    import { useSelection } from "../stores/selectionStore";
    import { Editor } from "@tiptap/core";
    import { hashtagDropdownVisible } from "../extensions/Hashtag";
    import { hashtagStore } from "../stores/hashtagStore";

    // Import smaller components
    import EditorToolbar from "./EditorToolbar.svelte";

    // Import utility functions
    import { createEditorExtensions } from "../utils/editorExtensions";
    import {
        syncContentWithDebounce,
        extractHashtagsFromEditor,
        focusEditor as focusEditorHelper,
        transformBlock as transformBlockHelper,
        insertLink as insertLinkHelper,
        handleHashtagClick as handleHashtagClickHelper,
        initializeHashtags,
    } from "../utils/editorHelpers";

    export let block;
    const dispatch = createEventDispatcher();

    let editor;
    let editorElement;
    let bubbleMenuElement;
    let tiptapEditor;
    let initialRender = true;
    let activeHashtags = [];

    // Subscribe to dropdown visibility for debugging
    const unsubscribeDropdown = hashtagDropdownVisible.subscribe(
        (isVisible) => {
            if (isVisible) {
                logger.debug("Hashtag dropdown opened", { blockId: block.id });
            }
        },
    );

    const { saveSelection, restoreSelection } = useSelection();

    // Check if block is empty
    export function isEmpty() {
        if (!tiptapEditor) return true;
        return tiptapEditor.isEmpty;
    }

    // Expose a method to focus this editor
    export function focusEditor(position = "end") {
        return focusEditorHelper(tiptapEditor, block.id, position);
    }

    // Transform block to a different type
    export function transformBlock(type) {
        transformBlockHelper(tiptapEditor, type, block.id, dispatch);
    }

    // Insert a link at the current selection
    export function insertLink(url, text) {
        insertLinkHelper(tiptapEditor, url, text);
    }

    // Handle clicks on hashtags
    function handleHashtagClick(hashtag) {
        handleHashtagClickHelper(hashtag, block.id, dispatch);
    }

    onMount(async () => {
        if (!editorElement) return;

        // Make sure dropdown is not visible on mount
        hashtagDropdownVisible.set(false);

        // Wait for DOM to be fully ready
        await tick();

        // Add additional timeout to ensure DOM is fully ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Initialize TipTap editor with custom extensions
        tiptapEditor = new Editor({
            element: editorElement,
            extensions: createEditorExtensions(
                bubbleMenuElement,
                handleHashtagClick,
            ),
            content: block.content || "",
            autofocus: false,
            onUpdate: () => {
                syncContentWithDebounce(
                    tiptapEditor,
                    block,
                    dispatch,
                    activeHashtags,
                    (updatedTags) => {
                        activeHashtags = updatedTags;
                    },
                );
            },
            onSelectionUpdate: () => {
                // This helps track the current selection for restoration
                saveSelection(editorElement);
            },
            onBlur: () => {
                syncContentWithDebounce(
                    tiptapEditor,
                    block,
                    dispatch,
                    activeHashtags,
                    (updatedTags) => {
                        activeHashtags = updatedTags;
                    },
                );
            },
        });

        // Pass keyboard events up to parent
        editorElement.addEventListener("keydown", (event) => {
            dispatch("keydown", event);
        });

        // Extract initial hashtags if the block already has content
        if (block.content) {
            initializeHashtags(tiptapEditor, block.id, (updatedTags) => {
                activeHashtags = updatedTags;
            });
        }
    });

    onDestroy(() => {
        // Make sure dropdown state is reset when component is destroyed
        hashtagDropdownVisible.set(false);

        // Clean up subscription
        unsubscribeDropdown();

        if (tiptapEditor) {
            // Clean up hashtags when the component is destroyed
            hashtagStore.deleteBlock(block.id);
            tiptapEditor.destroy();
        }
    });
</script>

<div class="editor-wrapper">
    <!-- Formatting bubble menu that appears on text selection -->
    <EditorToolbar bind:element={bubbleMenuElement} editor={tiptapEditor} />

    <div bind:this={editorElement} class="editor-content" on:keydown></div>
</div>

<style>
    /* Keep only component-specific styles here */
    /* Global styles are moved to editorStyles.js */
    .editor-wrapper {
        position: relative;
        width: 100%;
    }

    .editor-content {
        flex: 1;
        padding: 6px 10px;
        min-height: 24px;
        outline: none;
        background-color: #fcf5e5;
        border-radius: 5px;
        font-size: 18px;
    }
</style>
